import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  PRERENDER_LOCALE,
  PUBLIC_PAGES,
  absoluteUrl,
  localePath,
  pageAlternates,
  pageImage,
  pathLocale,
  seoLocale,
  type PublicPageKey,
} from './site';

/** Resolved in vite.config.ts from `VITE_SITE_URL` (default `DEFAULT_SITE_URL`). */
const SITE_URL = import.meta.env.VITE_SITE_URL;

function metaTag(attribute: 'name' | 'property', key: string): () => HTMLElement {
  return () => {
    const element = document.createElement('meta');
    element.setAttribute(attribute, key);
    return element;
  };
}

function canonicalTag(): HTMLElement {
  const element = document.createElement('link');
  element.rel = 'canonical';
  return element;
}

function alternateTag(hreflang: string): () => HTMLElement {
  return () => {
    const element = document.createElement('link');
    element.rel = 'alternate';
    element.hreflang = hreflang;
    return element;
  };
}

/** Set one attribute of a head element, adding the element if missing. Returns the undo. */
function setHeadAttribute(
  selector: string,
  create: () => HTMLElement,
  attribute: string,
  value: string,
): () => void {
  const existing = document.head.querySelector<HTMLElement>(selector);
  const element = existing ?? document.head.appendChild(create());
  const previous = element.getAttribute(attribute);
  element.setAttribute(attribute, value);
  return () => {
    if (!existing) element.remove();
    else if (previous === null) element.removeAttribute(attribute);
    else element.setAttribute(attribute, previous);
  };
}

/**
 * Keeps the document head on a public page's title, description, canonical URL and language
 * addresses, in the visitor's language, and puts back what was there on leaving. The canonical
 * is the address opened: `/tr/login` for the Turkish copy, `/login` otherwise. The prerendered
 * HTML already carries the same tags for a crawler (scripts/prerender.mjs); this covers moving
 * between pages inside the app and a switch of language.
 */
export function usePageHead(key: PublicPageKey): void {
  const { i18n } = useTranslation();
  const locale = seoLocale(i18n.resolvedLanguage ?? i18n.language);
  const addressLocale = pathLocale(useLocation().pathname) ?? PRERENDER_LOCALE;

  useEffect(() => {
    const page = PUBLIC_PAGES[key];
    const title = page.title[locale];
    const description = page.description[locale];
    const url = absoluteUrl(SITE_URL, localePath(page.path, addressLocale));
    const image = absoluteUrl(SITE_URL, pageImage(key).path);
    const previousTitle = document.title;
    document.title = title;
    const undo = [
      setHeadAttribute(
        'meta[name="description"]',
        metaTag('name', 'description'),
        'content',
        description,
      ),
      setHeadAttribute('link[rel="canonical"]', canonicalTag, 'href', url),
      ...pageAlternates(SITE_URL, page.path).map(({ hreflang, href }) =>
        setHeadAttribute(
          `link[rel="alternate"][hreflang="${hreflang}"]`,
          alternateTag(hreflang),
          'href',
          href,
        ),
      ),
      setHeadAttribute(
        'meta[property="og:title"]',
        metaTag('property', 'og:title'),
        'content',
        title,
      ),
      setHeadAttribute(
        'meta[property="og:description"]',
        metaTag('property', 'og:description'),
        'content',
        description,
      ),
      setHeadAttribute('meta[property="og:url"]', metaTag('property', 'og:url'), 'content', url),
      setHeadAttribute(
        'meta[property="og:image"]',
        metaTag('property', 'og:image'),
        'content',
        image,
      ),
      setHeadAttribute('meta[name="twitter:image"]', metaTag('name', 'twitter:image'), 'content', image),
    ];
    return () => {
      document.title = previousTitle;
      undo.reverse().forEach((restore) => restore());
    };
  }, [key, locale, addressLocale]);
}
